<script setup lang="ts">
import { AxiosError } from 'axios' // Import AxiosError
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { isPlausibleEmail } from '@/utils/emailAddress'
import { usesKitEmailDomain } from '@/utils/kitEmailDomains'

import userService from '../../services/userService'

definePage({
  meta: {
    title: 'pages.user.register.title',
    requiresAuth: false,
    breadcrumbs: [
      {
        label: 'user',
      },
      {
        label: 'register',
        icon: 'i-lucide-user-plus',
      },
    ],
  },
})

const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const { t } = useI18n()

const register = async () => {
  if (!email.value || !password.value || !confirmPassword.value) {
    errorMessage.value = t('pages.user.register.errors.requiredFields')
    return
  }

  if (!isPlausibleEmail(email.value)) {
    errorMessage.value = t('pages.user.register.errors.invalidEmail')
    return
  }

  if (usesKitEmailDomain(email.value)) {
    errorMessage.value = t('pages.user.register.errors.kitEmailDomain')
    return
  }

  if (password.value !== confirmPassword.value) {
    errorMessage.value = t('pages.user.register.errors.passwordMismatch')
    return
  }

  try {
    loading.value = true
    errorMessage.value = ''
    const result = await userService.register(email.value, password.value, confirmPassword.value)

    successMessage.value = result.requiresPasswordSetup
      ? t('pages.user.register.guestClaimMessage')
      : t('pages.user.register.successMessage')
    email.value = ''
    password.value = ''
    confirmPassword.value = ''
  } catch (error: unknown) {
    // Explicitly type error as unknown
    console.error('Registration error:', error)

    // Check if it's an AxiosError with a response and data
    if (error instanceof AxiosError && error.response?.data) {
      const errorData = error.response.data as any // Type assertion might be needed depending on exact API response structure

      // Handle structured error response using i18n
      if (
        errorData.errors &&
        typeof errorData.errors === 'object' &&
        Object.keys(errorData.errors).length > 0
      ) {
        const errorKeys = Object.keys(errorData.errors)
        const firstErrorKey = errorKeys[0]
        errorMessage.value = t(`pages.user.register.errors.api.${firstErrorKey}`)
      } else {
        // Fallback if 'errors' object is missing or empty
        const fallbackTitle =
          errorData.title || t('pages.user.register.errors.genericRegistrationError')
        errorMessage.value = t(
          'pages.user.register.errors.genericRegistrationError',
          { message: fallbackTitle },
          fallbackTitle,
        )
      }
    } else {
      // Fallback for non-Axios errors or errors without response data
      const fallbackMessage =
        error instanceof Error
          ? error.message
          : t('pages.user.register.errors.genericRegistrationError')
      errorMessage.value = t(
        'pages.user.register.errors.genericRegistrationError',
        { message: fallbackMessage },
        fallbackMessage,
      )
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div style="max-width: 400px" class="mycard px-4 py-2">
    <div class="mb-4 flex flex-row justify-between">
      <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-200">
        {{ t('pages.user.register.title') }}
      </h1>
    </div>
    <p class="mb-4 text-muted">
      {{ $t('pages.user.register.intro') }}
    </p>

    <AppAlert v-if="errorMessage" color="error" :closable="false" class="mb-4">
      {{ errorMessage }}
    </AppAlert>
    <AppAlert v-if="successMessage" color="success" :closable="false" class="mb-4">
      {{ successMessage }}
    </AppAlert>

    <form class="flex flex-col gap-3" @submit.prevent="register">
      <AppFloatField variant="on">
        <UInput
          id="email"
          v-model="email"
          class="w-full"
          autocomplete="username"
          :disabled="!!successMessage"
        />
        <label for="email">{{ t('fields.user.email') }}</label>
      </AppFloatField>
      <AppFloatField variant="on">
        <AppPassword
          id="password"
          v-model="password"
          class="w-full"
          autocomplete="new-password"
          :disabled="!!successMessage"
          toggle-mask
          feedback
        />
        <label for="password">{{ t('pages.user.register.password') }}</label>
      </AppFloatField>

      <AppFloatField variant="on">
        <AppPassword
          id="confirmPassword"
          v-model="confirmPassword"
          autocomplete="new-password"
          :disabled="!!successMessage"
          class="w-full"
          toggle-mask
        />
        <label for="confirmPassword">{{ t('pages.user.register.confirmPassword') }}</label>
      </AppFloatField>

      <div class="mt-2 flex flex-row gap-2">
        <UButton
          type="submit"
          :label="t('pages.user.register.submit')"
          class="w-full"
          :loading="loading"
          :disabled="!email || !password || !confirmPassword || !!successMessage"
        />
      </div>

      <div class="mt-3 flex flex-row justify-center">
        <span class="mr-2 text-muted">
          {{ t('pages.user.register.alreadyRegistered') }}
        </span>
        <router-link
          to="/user/login"
          class="cursor-pointer font-semibold text-blue-700 dark:text-blue-400"
        >
          {{ t('pages.user.register.login') }}
        </router-link>
      </div>
    </form>
  </div>
</template>
